import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { FiImage, FiType, FiLink, FiUpload, FiRotateCcw, FiCheck } from "react-icons/fi";
import { endPoints, siteConfig } from "../../../config/end-points";
import { authActions } from "../../../store/auth";
import { fetchLocalData, saveLocalData } from "../../../helpers/storage-helper";
import "../settings.css";
import "./whitelabel.css";

// Hardcoded defaults — not from siteConfig getters (which return whitelabel values)
const SITE_NAME = "SiteBuilder";
const DEFAULT_LOGO = `${window.location.origin}${process.env.PUBLIC_URL}/assets/images/sitebuilder_logo.png`;
const DEFAULT_FAVICON = `${window.location.origin}${process.env.PUBLIC_URL}/assets/images/favicon-sitebuilder.png`;
const DEFAULT_COPYRIGHT = `Copyright © ${new Date().getFullYear()} ${SITE_NAME}. All rights reserved.`;

const defaultFormData = {
  site_title: SITE_NAME,
  logo: DEFAULT_LOGO,
  favicon: DEFAULT_FAVICON,
  footer_text: DEFAULT_COPYRIGHT,
};

const Whitelabel = () => {
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({ ...defaultFormData });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);

  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    dispatch(authActions.setLoaderStatus({ status: false }));
    fetchWhitelabel();
  }, []);

  const fetchWhitelabel = async () => {
    try {
      const res = await axios.get(endPoints.whitelabelGet, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === 200 && res.data.body) {
        const data = res.data.body;
        setFormData({
          site_title: data.site_title || defaultFormData.site_title,
          logo: data.logo || defaultFormData.logo,
          favicon: data.favicon || defaultFormData.favicon,
          footer_text: data.footer_text || defaultFormData.footer_text,
        });
      }
    } catch (err) {
      console.error("Failed to fetch whitelabel settings");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (file, field) => {
    const setUploading = field === "logo" ? setLogoUploading : setFaviconUploading;
    setUploading(true);

    try {
      const uploadData = new FormData();
      uploadData.append("image", file);

      const res = await axios.post(endPoints.mediaUpload, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.status === 200 && res.data.image?.url) {
        setFormData((prev) => ({ ...prev, [field]: res.data.image.url }));
      }
    } catch (err) {
      console.error("Upload failed:", err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const res = await axios.post(endPoints.whitelabelSave, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === 200) {
        setMessage("Settings saved successfully!");
        dispatch(authActions.setWhitelabel(formData));
        // Update localStorage so siteConfig getters reflect new branding immediately
        const stored = fetchLocalData("sitebuilder_user");
        if (stored) {
          stored.whitelabel = formData;
          saveLocalData("sitebuilder_user", stored);
        }
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (err) {
      setMessage("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const resetField = (field) => {
    setFormData((prev) => ({ ...prev, [field]: defaultFormData[field] }));
  };

  return (
    <section className="mid_wrap st-settings">
      {/* Hero Header */}
      <div className="st-hero">
        <div className="container st-hero-container">
          <div className="st-hero-top">
            <div>
              <h1 className="st-hero-title">Whitelabel</h1>
              <p className="st-hero-sub">
                Customize your brand identity with your own logo, favicon, and brand name
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="st-body">
        <div className="container st-body-container">
          <div className="st-panel">
            <div className="wl-page">
              <div className="wl-form-card">
                <h2 className="wl-section-title">
                  <FiImage size={20} />
                  Brand Configuration
                </h2>

                <form onSubmit={handleSave}>
                  <div className="wl-form-grid">
                    {/* Left Column */}
                    <div className="wl-form-column">
                      {/* Brand Name */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiType size={14} />
                          Brand Name
                        </label>
                        <input
                          type="text"
                          name="site_title"
                          className="wl-input"
                          value={formData.site_title}
                          onChange={handleChange}
                          placeholder={defaultFormData.site_title}
                        />
                      </div>

                      {/* Footer Text */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiType size={14} />
                          Footer Text
                        </label>
                        <input
                          type="text"
                          name="footer_text"
                          className="wl-input"
                          value={formData.footer_text}
                          onChange={handleChange}
                          placeholder={defaultFormData.footer_text}
                        />
                      </div>

                      {/* Logo URL */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiLink size={14} />
                          Logo URL
                        </label>
                        <input
                          type="text"
                          name="logo"
                          className="wl-input"
                          value={formData.logo}
                          onChange={handleChange}
                          placeholder="https://example.com/logo.png"
                        />
                      </div>

                      {/* Logo Upload */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiImage size={14} />
                          Logo Preview
                        </label>
                        <div
                          className="wl-upload-card"
                          onClick={() => logoInputRef.current?.click()}
                        >
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              if (e.target.files[0]) handleImageUpload(e.target.files[0], "logo");
                            }}
                          />
                          {formData.logo ? (
                            <img src={formData.logo} alt="Logo" className="wl-preview-img" />
                          ) : (
                            <div className="wl-upload-placeholder">
                              <FiUpload size={24} />
                              <span>Click to upload logo</span>
                            </div>
                          )}
                          {logoUploading && <div className="wl-uploading">Uploading...</div>}
                        </div>
                        <div className="wl-dimension-badge">Recommended: 140 x 51 pixels</div>
                        <button
                          type="button"
                          className="wl-reset-btn"
                          onClick={() => resetField("logo")}
                        >
                          <FiRotateCcw size={12} />
                          Reset to Default
                        </button>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="wl-form-column">
                      {/* Favicon URL */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiLink size={14} />
                          Favicon URL
                        </label>
                        <input
                          type="text"
                          name="favicon"
                          className="wl-input"
                          value={formData.favicon}
                          onChange={handleChange}
                          placeholder="https://example.com/favicon.png"
                        />
                      </div>

                      {/* Favicon Upload */}
                      <div className="wl-group">
                        <label className="wl-label">
                          <FiImage size={14} />
                          Favicon Preview
                        </label>
                        <div className="wl-favicon-preview">
                          <div
                            className="wl-favicon-box"
                            onClick={() => faviconInputRef.current?.click()}
                          >
                            <input
                              ref={faviconInputRef}
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                if (e.target.files[0]) handleImageUpload(e.target.files[0], "favicon");
                              }}
                            />
                            {formData.favicon ? (
                              <img src={formData.favicon} alt="Favicon" className="wl-favicon-img" />
                            ) : (
                              <FiUpload size={20} />
                            )}
                            {faviconUploading && <div className="wl-uploading">Uploading...</div>}
                          </div>
                          <div className="wl-favicon-info">
                            <div className="wl-dimension-badge">Recommended: 32 x 32 pixels</div>
                            <button
                              type="button"
                              className="wl-reset-btn"
                              onClick={() => resetField("favicon")}
                            >
                              <FiRotateCcw size={12} />
                              Reset to Default
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="wl-submit-section">
                    {message && (
                      <span className={`wl-message ${message.includes("success") ? "wl-success" : "wl-error"}`}>
                        {message}
                      </span>
                    )}
                    <button type="submit" className="wl-submit-btn" disabled={saving}>
                      <FiCheck size={16} />
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Whitelabel;
